import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Moon, Sun, GraduationCap, ChevronDown } from "lucide-react";
import { Button } from "../ui/button";
import { useTheme } from "../../context/ThemeContext";
import { useLeadModal } from "../../context/LeadModalContext";
import { SERVICES } from "../../data/services";
import { NAV } from "../../constants/testIds";

export function Navbar() {
	const { theme, toggle } = useTheme();
	const { openLeadModal } = useLeadModal();
	const [scrolled, setScrolled] = useState(false);
	const [mobileOpen, setMobileOpen] = useState(false);
	const [servicesOpen, setServicesOpen] = useState(false);
	const location = useLocation();

	useEffect(() => {
		const onScroll = () => setScrolled(window.scrollY > 20);
		window.addEventListener("scroll", onScroll);
		return () => window.removeEventListener("scroll", onScroll);
	}, []);

	useEffect(() => { setMobileOpen(false); setServicesOpen(false); }, [location]);

	return (
		<header
			data-testid={NAV.navbar}
			className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
				scrolled
					? "bg-white/85 dark:bg-slate-950/85 backdrop-blur-xl border-b border-slate-200/80 dark:border-slate-800/80"
					: "bg-transparent"
			}`}
		>
			<nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 sm:h-18">
				<Link to="/" data-testid={NAV.brand} className="flex items-center gap-2 py-4">
					<span className="grid place-items-center h-9 w-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
						<GraduationCap className="h-5 w-5" />
					</span>
					<span className="font-heading font-extrabold text-lg text-slate-900 dark:text-white">
						SchoolXpert<span className="text-emerald-600 dark:text-emerald-400">360</span>
					</span>
				</Link>

				<div className="hidden lg:flex items-center gap-1">
					<Link to="/" className="px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:text-emerald-600 dark:hover:text-emerald-400">Home</Link>
					<div className="relative"
						onMouseEnter={() => setServicesOpen(true)}
						onMouseLeave={() => setServicesOpen(false)}>
						<button className="px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:text-emerald-600 dark:hover:text-emerald-400 flex items-center gap-1">
							Services <ChevronDown className="h-3.5 w-3.5" />
						</button>
						{servicesOpen && (
							<div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 w-[520px]">
								<div className="grid grid-cols-2 gap-1 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 shadow-xl">
									{SERVICES.map((s) => (
										<Link key={s.id} to={`/services/${s.id}`}
											className="flex items-start gap-3 rounded-xl p-3 hover:bg-slate-50 dark:hover:bg-slate-800">
											<s.icon className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
											<span>
												<span className="block text-sm font-semibold text-slate-900 dark:text-white">{s.title}</span>
												<span className="block text-xs text-slate-500 dark:text-slate-400">{s.tagline}</span>
											</span>
										</Link>
									))}
								</div>
							</div>
						)}
					</div>
					<Link to="/contact" className="px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:text-emerald-600 dark:hover:text-emerald-400">Contact</Link>
				</div>

				<div className="flex items-center gap-2">
					<button data-testid={NAV.themeToggle} onClick={toggle}
						className="grid place-items-center h-9 w-9 rounded-full text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800">
						{theme === "dark" ? <Sun className="h-4.5 w-4.5" /> : <Moon className="h-4.5 w-4.5" />}
					</button>
					<Button
						data-testid={NAV.ctaButton}
						onClick={() => openLeadModal({ sourcePage: "Homepage" })}
						className="hidden sm:inline-flex rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-semibold"
					>
						Get Growth Audit
					</Button>
					<button data-testid={NAV.mobileMenuButton} onClick={() => setMobileOpen((o) => !o)}
						className="lg:hidden grid place-items-center h-9 w-9 rounded-full text-slate-700 dark:text-slate-200">
						{mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
					</button>
				</div>
			</nav>

			{mobileOpen && (
				<div className="lg:hidden border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 px-4 py-4 space-y-1 max-h-[80vh] overflow-y-auto">
					<Link to="/" className="block px-3 py-2 rounded-lg text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800">Home</Link>
					<p className="px-3 pt-3 pb-1 text-xs font-bold uppercase tracking-wider text-slate-400">Services</p>
					{SERVICES.map((s) => (
						<Link key={s.id} to={`/services/${s.id}`}
							className="flex items-center gap-2 px-3 py-2 rounded-lg text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800">
							<s.icon className="h-4 w-4 text-emerald-600" /> {s.title}
						</Link>
					))}
					<Link to="/contact" className="block px-3 py-2 rounded-lg text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800">Contact</Link>
					<Button onClick={() => openLeadModal({ sourcePage: "Homepage" })}
						className="w-full mt-3 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white">
						Get Growth Audit
					</Button>
				</div>
			)}
		</header>
	);
}
